/**
 * 메뉴 가용성 표시 유틸 (#279, #280)
 *
 * BE `MenuAvailabilityInfo`의 `next_available_at` / `time_slots`를 사용자 친화적
 * 문자열로 변환. 로케일별 표현을 일관되게 관리하고 매퍼/컴포넌트의 부담을 분리.
 */

import type { Locale } from '@/config/locales';
import { logger } from '@/utils/logger';

const HH_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

/**
 * 매장 기본 타임존 — **임시 폴백** (BE 노출 전까지).
 *
 * ⚠️ 알려진 한계: 멕시코는 단일 시장이지만 4개 타임존 운영 중 (BE 매장 설정 enum):
 *   - America/Mexico_City (CST, UTC-6) — 가장 인구 밀집, 폴백 기본값
 *   - America/Cancun (EST, UTC-5)
 *   - America/Mazatlan (MST, UTC-7)
 *   - America/Tijuana (PST, UTC-8)
 *
 * 칸쿤·마사틀란·티후아나 매장은 본 폴백으로 표기 시 1~2시간 어긋남.
 * 그러나 사용자 디바이스 로컬 TZ 폴백보다는 정확도 우월(특히 해외 여행자 케이스).
 *
 * 해소: BE Store 응답에 timezone 필드 노출 후 매장별 값 사용
 * → mx-olulo/olulo-mx-admin#1128 (Resources 노출 작업)
 */
export const DEFAULT_STORE_TIMEZONE = 'America/Mexico_City';

/**
 * 매장 IANA 타임존 기준으로 시각 부분을 추출.
 * 인자 형식이 잘못되면 폴백으로 브라우저 로컬을 사용하고 logger.warn.
 */
function partsInTimeZone(
  d: Date,
  timeZone?: string,
): { year: number; month: number; day: number; hour: number; minute: number } {
  if (!timeZone) {
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
    };
  }
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = Object.fromEntries(
      fmt.formatToParts(d).map((p) => [p.type, p.value]),
    );
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour === '24' ? '00' : parts.hour),
      minute: Number(parts.minute),
    };
  } catch (err) {
    logger.warn('[menuAvailability] 타임존 변환 실패 — 브라우저 로컬 폴백', { timeZone, err });
    return partsInTimeZone(d);
  }
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

const TOMORROW_PREFIX = {
  ko: '내일',
  es: 'Mañana',
  en: 'Tomorrow',
  pt: 'Amanhã',
  fr: 'Demain',
  zh: '明天',
  ja: '明日',
  hi: 'कल',
  de: 'Morgen',
  it: 'Domani',
  ru: 'Завтра',
  ar: 'غدًا',
  vi: 'Ngày mai',
  th: 'พรุ่งนี้',
  nl: 'Morgen',
} satisfies Record<Locale, string>;

const NEXT_AVAILABLE_PREFIX = {
  ko: '다음 가능',
  es: 'Próximo',
  en: 'Next',
  pt: 'Próximo',
  fr: 'Prochain',
  zh: '下次可用',
  ja: '次回',
  hi: 'अगला',
  de: 'Nächste',
  it: 'Prossimo',
  ru: 'Следующее',
  ar: 'التالي',
  vi: 'Tiếp theo',
  th: 'ครั้งถัดไป',
  nl: 'Volgende',
} satisfies Record<Locale, string>;

const TIME_SLOTS_LABEL = {
  ko: '판매 시간',
  es: 'Horario de venta',
  en: 'Available hours',
  pt: 'Horário de venda',
  fr: 'Horaires de vente',
  zh: '可售时间',
  ja: '販売時間',
  hi: 'बिक्री का समय',
  de: 'Verkaufszeit',
  it: 'Orario di vendita',
  ru: 'Время продажи',
  ar: 'ساعات البيع',
  vi: 'Giờ bán',
  th: 'เวลาขาย',
  nl: 'Verkooptijden',
} satisfies Record<Locale, string>;

/**
 * BE `next_available_at` ISO 문자열을 사용자 친화적 라벨로 변환.
 *
 * - 오늘(또는 약간 과거 — 클럭 스큐 허용)이면 시각만: "17:00"
 * - 내일이면 prefix + 시각: "내일 09:30" / "Mañana 09:30" / "Tomorrow 09:30"
 * - 모레 이후(현재 BE 계약상 7일 이내)는 "M/D HH:MM" — 연도는 단기 계약상 생략
 *
 * 잘못된 입력(파싱 불가, 1일 초과 과거)은 `null` 반환 + logger.warn.
 *
 * @param iso BE가 내려준 ISO 8601 문자열 (예: "2026-04-23T17:00:00-06:00")
 * @param language 표시 로케일
 * @param now (테스트용 주입) 현재 시각 — 기본 `new Date()`
 * @param timeZone (선택) 매장 IANA TZ — 지정 시 매장 기준 시각으로 표기 (해외 사용자 대응)
 */
export function formatNextAvailableAt(
  iso: string | null | undefined,
  language: Locale,
  now: Date = new Date(),
  timeZone?: string,
): string | null {
  if (!iso) return null;

  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) {
    logger.warn('[menuAvailability] formatNextAvailableAt: 잘못된 ISO', { iso });
    return null;
  }

  const t = partsInTimeZone(target, timeZone);
  const n = partsInTimeZone(now, timeZone);

  const targetEpoch = Date.UTC(t.year, t.month - 1, t.day);
  const nowEpoch = Date.UTC(n.year, n.month - 1, n.day);
  const dayDiff = Math.round((targetEpoch - nowEpoch) / 86_400_000);

  if (dayDiff < -1) {
    logger.warn('[menuAvailability] formatNextAvailableAt: 비정상 과거 시각 폐기', {
      iso,
      dayDiff,
    });
    return null;
  }

  const time = `${pad2(t.hour)}:${pad2(t.minute)}`;

  if (dayDiff <= 0) return time;

  if (dayDiff === 1) {
    return `${TOMORROW_PREFIX[language]} ${time}`;
  }

  return `${t.month}/${t.day} ${time}`;
}

/**
 * 메뉴 가용 시간대(`time_slots`)를 사용자 친화적 문자열로 변환.
 *
 * - 빈 배열 또는 모든 슬롯이 무효(from/until null)면 `null` 반환 — 표시 스킵
 * - `from`/`until`은 BE에서 `HH:MM:SS` 형태로 옴 → `HH:MM`만 표시
 * - 부분 누락 또는 형식 오염 슬롯은 logger.warn 후 스킵
 * - 2개 슬롯은 ", "로 연결 (예: "11:00-15:00, 17:00-21:00")
 *
 * @param slots BE `is_available_at.time_slots`
 */
export function formatTimeSlots(
  slots: ReadonlyArray<{ from: string | null; until: string | null }> | null | undefined,
): string | null {
  if (!slots || slots.length === 0) return null;

  const formatted: string[] = [];
  let droppedCount = 0;

  for (const s of slots) {
    if (s.from == null || s.until == null) {
      droppedCount++;
      continue;
    }
    const fromMatch = HH_RE.exec(s.from);
    const untilMatch = HH_RE.exec(s.until);
    if (!fromMatch || !untilMatch) {
      droppedCount++;
      continue;
    }
    formatted.push(
      `${pad2(Number(fromMatch[1]))}:${fromMatch[2]}-${pad2(Number(untilMatch[1]))}:${untilMatch[2]}`,
    );
  }

  if (droppedCount > 0) {
    logger.warn('[menuAvailability] formatTimeSlots: 무효 슬롯 폐기', {
      droppedCount,
      total: slots.length,
    });
  }

  return formatted.length > 0 ? formatted.join(', ') : null;
}

/** "다음 판매 시각" 라벨용 로케일별 prefix. */
export function getNextAvailablePrefix(language: Locale): string {
  return NEXT_AVAILABLE_PREFIX[language];
}

/** "판매 시간" 라벨 (메뉴 상세 모달 등). */
export function getTimeSlotsLabel(language: Locale): string {
  return TIME_SLOTS_LABEL[language];
}
